import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { GitMerge, Loader2, Check, Link2Off, Info } from 'lucide-react';
import { P } from '../../lib/sitewalkApi';

const CONF_STYLE = { HIGH: 'bg-emerald-100 text-emerald-700', MEDIUM: 'bg-amber-100 text-amber-700', LOW: 'bg-slate-100 text-slate-600' };
const MATCH_LABEL = {
  EXACT_LOGICAL_LOCATION: 'Same logical location', FLOOR_PLAN_POINT_MATCH: 'Floor-plan-positioned',
  CONTEXT_MATCH: 'Context-matched', USER_CONFIRMED_MATCH: 'User-confirmed',
};

/** Match Assistant — deterministic suggestions; engineer confirms/corrects (never silent merge). */
export default function MatchAssistant({ projectId }) {
  const [walks, setWalks] = useState([]);
  const [walkId, setWalkId] = useState('');
  const [points, setPoints] = useState([]);
  const [cp, setCp] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { axios.get(`${P(projectId)}/site-walks`, { params: { pageSize: 200 } }).then((r) => setWalks(r.data.items || [])); }, [projectId]);
  useEffect(() => {
    if (!walkId) { setPoints([]); return; }
    axios.get(`${P(projectId)}/site-walks/${walkId}/capture-points`).then((r) => setPoints(r.data.items || []));
    setCp(null); setSuggestions(null);
  }, [walkId, projectId]);

  const loadMatches = async (point) => {
    setCp(point); setSuggestions(null); setLoading(true);
    try { setSuggestions((await axios.get(`${P(projectId)}/capture-points/${point.id}/matches`)).data.suggestions || []); }
    catch (e) { toast.error('Cannot load suggestions'); }
    setLoading(false);
  };

  const confirm = async (target) => {
    setBusy(true);
    try { await axios.post(`${P(projectId)}/capture-points/${cp.id}/confirm-match`, { targetCapturePointId: target }); toast.success('Location confirmed — history now shared'); await loadMatches(cp); }
    catch (e) { toast.error(e.response?.data?.detail || 'Confirm failed'); }
    setBusy(false);
  };
  const unlink = async () => {
    setBusy(true);
    try { await axios.post(`${P(projectId)}/capture-points/${cp.id}/correct-match`, {}); toast.success('Match corrected / unlinked'); await loadMatches(cp); }
    catch (e) { toast.error(e.response?.data?.detail || 'Correct failed'); }
    setBusy(false);
  };

  return (
    <div data-testid="match-assistant">
      <div className="flex items-center gap-2 mb-2 text-xs text-slate-500">
        <GitMerge className="w-4 h-4 text-sky-600" /> Deterministic suggestions only. Confirming links captures to the same logical location; nothing is merged automatically.
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <select value={walkId} onChange={(e) => setWalkId(e.target.value)} className="border rounded-lg px-2 py-1.5 text-xs w-full mb-2" data-testid="match-walk-select">
            <option value="">Select site walk…</option>
            {walks.map((w) => <option key={w.id} value={w.id}>{w.walkNumber}</option>)}
          </select>
          <div className="bg-white rounded-xl border border-slate-200 max-h-[60vh] overflow-y-auto p-1" data-testid="match-cp-list">
            {points.map((p) => (
              <button key={p.id} onClick={() => loadMatches(p)} className={`w-full text-left px-3 py-2 rounded-lg text-xs ${cp?.id === p.id ? 'bg-sky-50 border border-sky-300' : 'hover:bg-slate-50 border border-transparent'}`} data-testid={`match-cp-${p.sequence}`}>
                <div className="font-medium text-slate-700">{p.sequence}. {p.name}</div>
                <div className="text-[10px] text-slate-400">{p.captureLocationId ? 'Linked to a logical location' : 'Not linked yet'}</div>
              </button>
            ))}
            {walkId && points.length === 0 && <p className="text-xs text-slate-400 p-2">No capture points.</p>}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-3" data-testid="match-suggestions">
          {!cp ? <p className="text-sm text-slate-400">Select a capture point to see suggested existing locations.</p> : loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : (
            <>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-slate-700">Suggestions for {cp.name}</h4>
                {cp.captureLocationId && <button onClick={unlink} disabled={busy} className="text-[11px] flex items-center gap-1 text-red-600" data-testid="match-unlink"><Link2Off className="w-3.5 h-3.5" /> Unlink</button>}
              </div>
              {(suggestions || []).length === 0 ? <p className="text-xs text-slate-400 flex items-center gap-1"><Info className="w-3.5 h-3.5" /> No existing location suggested. You can Create New Location by capturing here.</p> : (
                <div className="space-y-1.5">
                  {suggestions.map((s) => (
                    <div key={s.id} className="rounded-lg border border-slate-100 px-2.5 py-2" data-testid={`match-suggestion-${s.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-slate-700">{s.walkNumber || 'Walk'} · {(s.capturedAt || '').slice(0, 10)}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${CONF_STYLE[s.confidence]}`}>{s.confidence}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{MATCH_LABEL[s.matchType] || s.matchType} — {s.explanation}</div>
                      <button onClick={() => confirm(s.id)} disabled={busy} className="mt-1.5 text-[11px] flex items-center gap-1 px-2.5 py-1 bg-sky-600 text-white rounded-lg" data-testid={`match-confirm-${s.id}`}><Check className="w-3.5 h-3.5" /> Confirm existing location</button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
